// This is for Draggable cards that are used in the DragNDropTasks component
// So things like the TaskDroppableCard and the DraggableCard are used here

import { useDraggable } from "@dnd-kit/core"
import { DraggableCardTaskProps } from "../../../../interfaces/draggable_types"
import { categoryIcon } from "../../../../api/utils"

type TaskCardProps = {
    id: string
    children: React.ReactNode
    parent: string | null
    disabled?: boolean
}

// All this does is just make the card draggable
export function TaskDroppableCard({id, children, disabled=false}: TaskCardProps){
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: id,
        disabled: disabled
    })

    // This helps with the animation of the draggable card
    // The opacity is set to 0 so that the card is hidden
    // <DragOverlay> is used to show the card when it is being dragged
    const style = transform ? {
        opacity: 0,
    } : undefined

    return (
        <button ref={setNodeRef} {...listeners} {...attributes} style={style}>
            {children}
        </button>
    )
}

type DraggableCardProps = DraggableCardTaskProps & {
    onToggleUnneeded?: (card_id: string) => void
}

/**
 * This is the card that is displayed in the DragNDropTasks component
 * @param id : this is generally a number that is used to identify the card or the task_id if it was already created
 * @param title : the string that is displayed in the card
 * @param category : the category of the card [Electrical, Plumbing, Mechanical, or Other]
 * @param parent : the parent of the card, this is the email of the employee that is assigned to the card
 * @param template_id : when present, marks this card as derived from a TaskTemplate and enables the unneeded toggle
 * @param is_unneeded : when true, the card is greyed out and drag is disabled
 * @param onToggleUnneeded : callback fired by the hover button
 */
export function DraggableCard({id, title, category, parent, template_id, is_unneeded, onToggleUnneeded}: DraggableCardProps){
    const color = category === 'Electrical' ? 'text-blue-800 dark:text-blue-200' : category === 'Plumbing' ? 'text-green-800 dark:text-green-200' : category === 'Mechanical' ? 'text-yellow-800 dark:text-yellow-200' : 'text-red-800 dark:text-red-200'
    const bgColor = category === 'Electrical' ? 'bg-blue-50 dark:bg-blue-800' : category === 'Plumbing' ? 'bg-green-50 dark:bg-green-800' : category === 'Mechanical' ? 'bg-yellow-50 dark:bg-yellow-800' : 'bg-red-50 dark:bg-red-800'

    const canToggle = template_id != null && onToggleUnneeded != null

    return (
        <div className="relative group">
            <TaskDroppableCard id={id} parent={parent} disabled={is_unneeded}>
                <div className={`text-left border border-stone-300 dark:border-stone-700 rounded-md p-2 m-2 ${bgColor} min-h-24 w-56 ${is_unneeded ? 'opacity-50' : ''}`}>
                    <div className="flex flex-row items-center">
                        {categoryIcon(category) && <img src={categoryIcon(category) ?? ''} alt="Task Icon" className="w-5 h-5 mr-2" />}
                        <p className={`${color} font-bold`}>{category}</p>
                    </div>
                    <h3 className='text-lg '>{title}</h3>
                </div>
            </TaskDroppableCard>

            {canToggle && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation()
                        onToggleUnneeded(id)
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className={`absolute top-3 right-3 z-10 px-2 py-1 text-xs font-medium rounded-md transition-opacity duration-150 ${is_unneeded ? 'opacity-100 bg-green-600 hover:bg-green-700 text-white' : 'opacity-0 group-hover:opacity-100 bg-stone-700 hover:bg-stone-800 text-white'}`}
                >
                    {is_unneeded ? 'Mark needed' : 'Mark unneeded'}
                </button>
            )}
        </div>
    )
}